import EventBus from '@/event-bus'
import { i18n } from '@/i18n'
import VisitorSettings from '@/types/VisitorSettings'
import VueCookies from 'vue-cookies'

interface VisitorSettingsState {
    visitorSettings: VisitorSettings
}

const state: VisitorSettingsState = {
    visitorSettings: {
        iso_week: true,
        language: 'en',
        locale: 'en-US',
        time_format: '24h',
        time_zone_name: 'Etc/UTC'
    }
}

const storeSettingsAndEmitChangedEvent = (settings: VisitorSettings): void => {
    localStorage.setItem(
        'visitor-settings',
        JSON.stringify(settings)
    )
    EventBus.$emit('visitor-settings-changed')
}

export default {
    state,

    getters: {
        dateFormat (state: VisitorSettingsState): string {
            return state.visitorSettings.locale
        },
        isoWeek (state: VisitorSettingsState): boolean {
            return state.visitorSettings.iso_week
        },
        language (state: VisitorSettingsState): string {
            return state.visitorSettings.language
        },
        timeFormat (state: VisitorSettingsState): string {
            return state.visitorSettings.time_format
        },
        timeZone (state: VisitorSettingsState): string {
            return state.visitorSettings.time_zone_name
        },
        visitorSettings (state: VisitorSettingsState): VisitorSettings {
            return state.visitorSettings
        }
    },

    mutations: {
        set (state: VisitorSettingsState, payload: VisitorSettings): void {
            state.visitorSettings = payload
            i18n.global.locale.value = payload.language
            EventBus.$emit('languageChanged', payload.language)
            document.getElementsByTagName('html')[0].lang = payload.language
            VueCookies.set('locale', payload.language)
            VueCookies.set('timezone', payload.time_zone_name)
            storeSettingsAndEmitChangedEvent(state.visitorSettings)
        },
        setParam<K extends keyof VisitorSettings>(state: VisitorSettingsState, { key, value }: { key: K; value: VisitorSettings[K] }): void {
            state.visitorSettings[key] = value
            switch (key) {
                case 'language':
                    i18n.global.locale.value = value
                    VueCookies.set('locale', value)
                    EventBus.$emit('languageChanged', value)
                    document.getElementsByTagName('html')[0].lang = `${value}`
                    break
                case 'time_zone_name':
                    VueCookies.set('timezone', value)
                    break
            }
            storeSettingsAndEmitChangedEvent(state.visitorSettings)
        }
    },

    actions: {
        initialize ({ rootGetters, commit }: { rootGetters: any, commit: any }): void {
            const localState: string | null =
                localStorage.getItem('visitor-settings')
            if (localState) {
                try {
                    const parsedState = JSON.parse(localState)
                    commit('set', parsedState)
                    return
                } catch (e) {
                    localStorage.removeItem('visitor-settings')
                }
            }

            const config = rootGetters['config']
            if (config) {
                commit('set', {
                    iso_week: config.locale.is_iso_week,
                    language: config.languages.default,
                    locale: config.locale.name,
                    time_format: config.locale.time_format,
                    time_zone_name: config.time_zone_name
                })
            }
        }
    },

    namespaced: true
}
